import type { CalculationOut, CaseStatus, HintOut } from "@/api";

export interface Readiness {
  /** German, one per obstacle — empty means the button may be pressed. */
  reasons: string[];
  /** How many price gaps the approval would carry (D-24). */
  gapCount: number;
  canApprove: boolean;
}

/** Shared with `CalculationBanner` (S-9): one place that decides between the
 * singular and the plural word for a count. */
export function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * An unmeasured wall's hint rule (E-65, D-14 Fassung 2): `WALL_HINT_PREFIX` in
 * `pricing/engine.py` plus the **one-based** wall number — one hint, so one
 * tick, per wall. Matched whole, so neither the old blocker code
 * (`walls_without_measure`) nor a bare prefix passes for a wall.
 */
const WALL_HINT_RULE = /^wall_without_measure:\d+$/;

/** Whether a hint's rule is the one of an unmeasured wall — not a price hint, not a planning finding. */
export function isWallHint(rule: string): boolean {
  return WALL_HINT_RULE.test(rule);
}

export interface GroupedHint {
  rule: string;
  message: string;
  lineNos: number[];
}

/**
 * One entry per distinct rule, not per hint object (fix round 2, Important
 * 3). `engine.py` (`~2417`) appends one `Hint` per *priced position* that
 * earns it — `band_gap`, `mesh_mapped`, `price_on_request`,
 * `mindestpreis_after_discount`, `quantity_over_100` all come from
 * `_price_position`/`_grating_line` per position, so a Vorgang with two
 * positions in the same band gap sends two hints sharing one rule. Only
 * *plan* findings were ever de-duplicated before this (`dto._plan_finding_wire`
 * groups those server-side); this does the same projection for every hint,
 * so `ApprovalPanel` renders one checkbox per rule (first message kept,
 * every hint's `lineNos` merged in order) instead of two bound to the one
 * `seen` entry `acknowledgedRules` (by rule name) already treats as one.
 */
export function groupHintsByRule(hints: readonly HintOut[]): GroupedHint[] {
  const order: string[] = [];
  const byRule = new Map<string, GroupedHint>();
  for (const hint of hints) {
    let group = byRule.get(hint.rule);
    if (group === undefined) {
      group = { rule: hint.rule, message: hint.message, lineNos: [] };
      byRule.set(hint.rule, group);
      order.push(hint.rule);
    }
    group.lineNos.push(...hint.lineNos);
  }
  return order.map((rule) => byRule.get(rule) as GroupedHint);
}

/**
 * The ticks a request may carry: the ticked rules that this calculation's
 * hints actually name, each once, in the hints' order. `seen` can outlive its
 * calculation — a draft kept across „Kunde zuordnen“, a wall that has a
 * measure by now — and the release record stores whatever is sent, so what
 * the calculation does not carry never reaches it.
 */
export function acknowledgedRulesOf(calc: CalculationOut, seen: ReadonlySet<string>): string[] {
  return groupHintsByRule(calc.hints)
    .map((hint) => hint.rule)
    .filter((rule) => seen.has(rule));
}

/**
 * Whether a calculation carries a standing approval (E-52) — not withdrawn.
 * Used to be inlined once in this file and once more in `CalculationScreen`
 * (Task 5 review, controller notes §6); the screen, the banner and the
 * approval panel now all call this one function instead of repeating the
 * `review.kind !== "withdrawn"` check.
 */
export function isReleased(calc: CalculationOut): boolean {
  return calc.review !== null && calc.review.kind !== "withdrawn";
}

/**
 * Whether the quote file is the offer — and if not, why (E-52, Task 13d):
 * `holds` while the release holds (released, and on the Stand the Vorgang
 * stands at — the server's own test, `rendering.is_released_quote`); a draft
 * otherwise, never released (`none`), taken back (`withdrawn`) or released
 * on a plan that is no longer the one on the board (`superseded`). The file
 * says the same three in its status line, so the button can name the one it
 * names (Zweitgutachten N4).
 */
export type QuoteRelease = "holds" | "none" | "withdrawn" | "superseded";

export function quoteRelease(calc: CalculationOut, latestVersionNo: number): QuoteRelease {
  if (calc.review === null) return "none";
  if (calc.review.kind === "withdrawn") return "withdrawn";
  return calc.stale || calc.planVersionNo !== latestVersionNo ? "superseded" : "holds";
}

/** Why „Freigeben“ waits on a Vorgang back in planning (B-1). */
export const REPLANNING_REASON = "Vorgang steht wieder in der Planung — neu rechnen.";

/**
 * Whether the calculation is current but the Vorgang stands in planning — the
 * shape „Wiederherstellen“ leaves (spec 2026-10-15 §5: always back to „In
 * Planung“, no new Stand). Nothing about the figures is wrong; the stage is.
 * „Neu rechnen“ answers with the same calculation and moves the Vorgang on
 * (D-16 hit, Task 4 ruling) — the one gesture between here and „Freigeben“.
 */
export function needsRerunForStage(calc: CalculationOut, latestVersionNo: number, caseStatus: CaseStatus): boolean {
  const stale = calc.stale || calc.planVersionNo !== latestVersionNo;
  return caseStatus === "planning_in_progress" && !stale && !isReleased(calc);
}

/**
 * D-13 for the button: the same gates `pricing.approvable` holds on the
 * server, computed here so a person is not offered a button that will
 * answer 409. The server still decides — a reason the server finds and this
 * list does not (a colleague's approval landing first) comes back as the
 * 409's own list (`refusalReasons`, Task 6).
 *
 * Blockers are a hard stop, every one of them, with no exemption for a
 * blocker whose `lineNos` happen to all be acknowledged gaps (fix round 2,
 * Important 5): `approvable()` passes every stored blocker through
 * unconditionally (`service.py`'s `blockers.extend(blockers_of(calculation))`)
 * and N-1 is explicit that `blockers` are never acknowledgeable — only
 * `gaps` are, and a blocker is never among them. This file used to read an
 * empty `lineNos` as "no line to point at, hence unconditional" and a
 * non-empty one covered by gaps as "already handled elsewhere"; the server
 * draws no such line, so neither does this.
 *
 * `vkTotal === null` is not a reason (fix round 2, Important 4, controller
 * ruling): `approvable()` compares `expected_vk_total != calculation.vk_total`,
 * and `None == None` passes there exactly as anywhere else — `ApproveIn`'s
 * own docstring says a calculation with no priced line at all approves with
 * `expectedVkTotal: null` (D-24). A Vorgang whose every line is a gap must
 * stay releasable once every gap is ticked, the same as one with a partial
 * sum.
 */
export function approvalReadiness(
  calc: CalculationOut,
  latestVersionNo: number,
  acknowledgedRules: ReadonlySet<string>,
  acknowledgedGaps: ReadonlySet<number>,
  caseStatus?: CaseStatus,
): Readiness {
  const reasons: string[] = [];
  const stale = calc.stale || calc.planVersionNo !== latestVersionNo;

  // The Vorgang's own stage is a gate the server holds too: the approval is
  // the move `calculation_ready | price_data_missing → quote_approved`, and
  // there is none from anywhere else. A cancelled Vorgang takes nothing; one
  // back in planning with its calculation still current — restored after a
  // cancel, the Stand unmoved (screen check 15.10.2026, B-1) — takes the
  // calculation up again on „Neu rechnen“, which answers with the same one
  // and moves the stage (Task 4 ruling). A stale one says so already below.
  if (caseStatus === "cancelled") reasons.push("Vorgang ist storniert — erst in der Akte wiederherstellen.");
  else if (caseStatus !== undefined && needsRerunForStage(calc, latestVersionNo, caseStatus)) {
    reasons.push(REPLANNING_REASON);
  }
  if (stale) reasons.push("Stand veraltet — neu rechnen.");
  if (calc.priceSource.priceStateChanged) reasons.push("Preisstand hat sich geändert — neu rechnen.");
  if (isReleased(calc)) reasons.push("Bereits freigegeben.");

  for (const blocker of calc.blockers) reasons.push(blocker.message);

  // The walls first, as the panel lists them, and in their own words: a wall
  // without a measure is a hint like any other to the server, but „2 Hinweise“
  // would not tell a person that two of them are the walls.
  const open = groupHintsByRule(calc.hints).filter((hint) => !acknowledgedRules.has(hint.rule));
  const openWalls = open.filter((hint) => isWallHint(hint.rule)).length;
  if (openWalls > 0) reasons.push(`${count(openWalls, "Wand", "Wände")} ohne Maß nicht quittiert.`);
  const openRules = open.length - openWalls;
  if (openRules > 0) reasons.push(`${count(openRules, "Hinweis", "Hinweise")} nicht als gesehen quittiert.`);

  const openGaps = calc.gaps.filter((gap) => !acknowledgedGaps.has(gap.lineNo)).length;
  if (openGaps > 0) reasons.push(`${count(openGaps, "Lücke", "Lücken")} nicht quittiert.`);

  return { reasons, gapCount: calc.gaps.length, canApprove: reasons.length === 0 };
}

/** „Kalkulation freigeben“ — or, with gaps, what the approval will carry (D-24). */
export function approveLabel(gapCount: number): string {
  if (gapCount === 0) return "Kalkulation freigeben";
  return `Mit ${count(gapCount, "Lücke", "Lücken")} freigeben`;
}
